/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router-dom';

import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import queryString from 'query-string';
import { MyThunkDispatch, RootState } from 'store';

import { setOAuthEmail } from 'action-creators/user';
import { onUserLogout, rappiFromOAuthLogin, rappiLogin } from 'actions/user';
import Loader from 'components/Loader';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { postExchange } from 'services/oauthService';
import { linkRappiUser } from 'services/registrationService';

const LoginToken = () => {
  const location = useLocation();
  const queryParams = queryString.parse(location.search) as {
    token: string;
    email?: string;
    code?: string;
  };
  const { token, email, code } = queryParams;

  const dispatch: MyThunkDispatch = useDispatch();
  const history = useHistory();
  const [isUserLoading, setUserLoading] = useState(false);
  const toast = useToast();
  const { t } = useStoreTranslation();
  const {
    user: { oAuthEmail },
  } = useSelector((state: RootState) => state);

  const executeLogin = async (code: string) => {
    dispatch(onUserLogout());
    setUserLoading(true);

    try {
      const user = await dispatch(rappiFromOAuthLogin(code.toString(), true));

      Sentry.addBreadcrumb({ category: 'oauth-token', message: 'Rappi login response', data: user });

      if (user.type === 'admin' && !user.onboardingDone) {
        history.replace(`/onboarding?email=${user.email}&name=${user.name}&lastName=${user.lastName}`);
      } else if (user.type.toLowerCase() === 'guest') {
        history.replace('/login-not-authorized');
      } else if (user.isGroupApprover) {
        history.replace('/dashboard');
      } else {
        history.replace('/booking');
      }
      setUserLoading(false);
    } catch (error) {
      Sentry.addBreadcrumb({
        category: 'login-token',
        message: 'Could not perform login by token',
        data: { error, message: error.message, response: error.response },
      });
      Sentry.captureMessage('[LoginToken] Could not perform login by token');
      history.replace('/login/confirm-email');
    }
  };

  const executeOAuthFromRegistration = async (code: string, state: string) => {
    setUserLoading(true);

    try {
      const responseOAuth = await postExchange({
        code: code,
        client_id: ENV.RAPPI_OAUTH.LOGIN_CLIENTID,
        grant_type: 'authorization_code',
        redirect_uri:
          state === 'registration'
            ? ENV.RAPPI_OAUTH.REDIRECT_URL_FROM_REGISTRATION
            : ENV.RAPPI_OAUTH.REDIRECT_URL_FROM_INVITE,
      });

      try {
        const responseLinkUser = await linkRappiUser({
          email: oAuthEmail ?? '',
          token: responseOAuth.access_token,
        });

        if (responseLinkUser.errors.some((error) => error.includes('user is already linked'))) {
          toast.add({
            type: 'success',
            message: t('registrationOnboarding.redirectToLinkedAccount'),
          });
        }
      } catch (e) {}

      try {
        const user = await dispatch(rappiLogin(responseOAuth.access_token.toString()));
        Sentry.addBreadcrumb({ category: 'login-token', message: 'Rappi login response', data: user });

        if (user) {
          if (user.hasBranchCompanies) {
            history.replace('/user-country-selection');
          } else {
            if (user.type === 'admin' && !user.onboardingDone) {
              history.replace(`/onboarding?email=${user.email}&name=${user.name}&lastName=${user.lastName}`);
            } else if (user.type.toLowerCase() === 'guest') {
              history.replace('/login-not-authorized');
            } else if (user.isGroupApprover) {
              history.replace('/dashboard');
            } else {
              history.replace('/booking');
            }
          }

          dispatch(setOAuthEmail(''));
        } else {
          history.replace('/login-not-authorized');
        }

        setUserLoading(false);
      } catch (error) {
        Sentry.addBreadcrumb({
          category: 'login-token',
          message: 'Could not perform login by token',
          data: { error, message: error.message, response: error.response },
        });
        Sentry.captureMessage('[LoginToken] Could not perform login by token');
        history.replace('login/confirm-email/not-found');
      }

      setUserLoading(false);
    } catch (error) {
      Sentry.addBreadcrumb({
        category: 'login-token',
        message: 'Could not perform login by token',
        data: { error, message: error.message, response: error.response },
      });
      Sentry.captureMessage('[LoginToken] Could not perform login by token');
      history.replace('/login/confirm-email');
    }
  };

  useEffect(() => {
    const login = async () => {
      Sentry.addBreadcrumb({ category: 'login-token', message: 'Logging in via token...', data: { token, email } });

      //Login via Rappi Token
      if (token) {
        dispatch(onUserLogout());
        setUserLoading(true);

        if (email) {
          history.replace(`/invite${location.search}`);
          return;
        }

        try {
          const user = await dispatch(rappiLogin(token.toString()));
          Sentry.addBreadcrumb({ category: 'login-token', message: 'Rappi login response', data: user });

          if (user) {
            if (user.hasBranchCompanies) {
              history.replace('/user-country-selection');
            } else {
              if (user.type === 'admin' && !user.onboardingDone) {
                history.replace(`/onboarding?email=${user.email}&name=${user.name}&lastName=${user.lastName}`);
              } else if (user.type.toLowerCase() === 'guest') {
                history.replace('/login-not-authorized');
              } else if (user.isGroupApprover) {
                history.replace('/dashboard');
              } else {
                history.replace('/booking');
              }
            }
          }

          setUserLoading(false);
        } catch (error) {
          Sentry.addBreadcrumb({
            category: 'login-token',
            message: 'Could not perform login by token',
            data: { error, message: error.message, response: error.response },
          });

          Sentry.withScope(function (scope) {
            scope.setTag('email', email ?? '');
            scope.setTag('token', token ?? '');

            Sentry.captureMessage('[LoginToken] Could not perform login by token');
          });

          history.replace('/login/confirm-email');
        }
      }
      //Login via Rappi Code (OAuth)
      else if (code) {
        //Login
        if (location.pathname === '/oauth-login') {
          executeLogin(code);
        }

        if (location.pathname === '/oauth-registration') {
          executeOAuthFromRegistration(code, 'registration');
        }

        if (location.pathname === '/oauth-invite') {
          executeOAuthFromRegistration(code, 'invite');
        }
      } else {
        history.replace('/login');
      }
    };

    login();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{isUserLoading && <Loader fullScreen />}</>;
};

export default LoginToken;
